import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../components/TableBox';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import useFetch from '../../../hooks/useFetch';
import usePagination from '../../../hooks/usePagination';
import TenantUserService from '../../../services/tenant-user.service';
import { usePermission } from '../../../hooks/usePermission';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';


export interface TenantUserRow {
  id: number;
  tenant_id: number;
  tenant_name: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'invited' | 'active' | 'inactive';
  invite_accepted_at: string | null;
}

interface TenantUserListPanelProps {
  /** When set, scopes the list to a single tenant (Tenant 360 → Users tab). Omit for the standalone S08 list. */
  tenantId?: number | string;
  showTenantColumn?: boolean;
}

const TenantUserListPanel: React.FC<TenantUserListPanelProps> = ({ tenantId, showTenantColumn = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManageUsers = usePermission('user:invite');

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'name' });
  const [items, setItems] = useState<TenantUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [deactivateTarget, setDeactivateTarget] = useState<TenantUserRow | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TenantUserRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const { loading } = useFetch({
    service: TenantUserService,
    func: 'list',
    payload: { tenantId, page, limit: perPage },
    dependency: [refreshToken, tenantId, page, perPage],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleResendInvite = useCallback(
    async (id: number) => {
      try {
        await TenantUserService.reinvite(id);
        toast.success(<Toast message={t('tenantUsers.list.pageTexts.resendSuccess')} />, { icon: false });
        setRefreshToken((v) => v + 1);
      } catch (err) {
        // interceptor already surfaces the error toast
      }
    },
    [t]
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.iam}${AuthRoutes.tenantUsers}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const closeDeactivateModal = useCallback(() => {
    setDeactivateTarget(null);
    setDeactivateReason('');
  }, []);

  const handleDeactivateConfirm = useCallback(async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await TenantUserService.deactivate(deactivateTarget.id, deactivateReason || undefined);
      toast.success(<Toast message={t('tenantUsers.list.pageTexts.deactivateSuccess')} />, { icon: false });
      setRefreshToken((v) => v + 1);
      closeDeactivateModal();
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [deactivateTarget, deactivateReason, closeDeactivateModal, t]);

  const closeDeleteModal = useCallback(() => {
    setDeleteTarget(null);
    setDeleteReason('');
    setDeleteReasonError('');
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    if (!deleteReason.trim()) {
      setDeleteReasonError(t('tenantUsers.list.pageTexts.deleteModal.reasonRequired'));
      return;
    }
    setActionLoading(true);
    try {
      await TenantUserService.remove(deleteTarget.id, deleteReason.trim());
      toast.success(<Toast message={t('tenantUsers.list.pageTexts.deleteSuccess')} />, { icon: false });
      setRefreshToken((v) => v + 1);
      closeDeleteModal();
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [deleteTarget, deleteReason, closeDeleteModal, t]);

  const headCells: HeadCellType[] = useMemo(() => {
    const cells: HeadCellType[] = [
      { id: 'name', sortEnabled: true, width: '18%', label: t('tenantUsers.list.pageTexts.table.columns.name') },
      { id: 'email', sortEnabled: true, width: '22%', label: t('tenantUsers.list.pageTexts.table.columns.email') },
    ];
    if (showTenantColumn) {
      cells.push({ id: 'tenant_name', sortEnabled: true, width: '15%', label: t('tenantUsers.list.pageTexts.table.columns.tenant') });
    }
    cells.push(
      { id: 'role', sortEnabled: true, width: '12%', label: t('tenantUsers.list.pageTexts.table.columns.role') },
      { id: 'status', sortEnabled: true, width: '11%', label: t('tenantUsers.list.pageTexts.table.columns.status') }
    );
    if (canManageUsers) {
      cells.push({ id: 'actions', sortEnabled: false, width: '22%', label: t('tenantUsers.list.pageTexts.table.columns.actions'), textAlign: 'right' });
    }
    return cells;
  }, [t, showTenantColumn, canManageUsers]);

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: TenantUserRow; className?: string }) => (
      <tr className={className}>
        <td>{item.name}</td>
        <td>{item.email}</td>
        {showTenantColumn && <td>{item.tenant_name}</td>}
        <td>{t(`tenantUsers.form.pageTexts.fields.role.options.${item.role}`)}</td>
        <td>
          <StatusBadge
            label={item.status}
            colour={item.status === 'active' ? '#2e7d32' : item.status === 'invited' ? '#ed6c02' : '#757575'}
          />
        </td>
        {canManageUsers && (
          <td className="text-end">
            <RowActions
              actions={[
                item.status === 'invited' && {
                  id: 'resend',
                  label: t('tenantUsers.list.pageTexts.resendInvite') || 'Resend Invite',
                  icon: <i className="ri-mail-send-line" />,
                  onClick: () => handleResendInvite(item.id),
                },
                {
                  id: 'edit',
                  label: t('tenantUsers.list.pageTexts.edit') || 'Edit',
                  icon: <i className="ri-pencil-line" />,
                  onClick: () => handleEdit(item.id),
                },
                item.status !== 'inactive' && {
                  id: 'deactivate',
                  label: t('tenantUsers.list.pageTexts.deactivate') || 'Deactivate',
                  icon: <i className="ri-forbid-line" />,
                  danger: true,
                  onClick: () => setDeactivateTarget(item),
                },
                {
                  id: 'delete',
                  label: t('tenantUsers.list.pageTexts.delete') || 'Delete',
                  icon: <i className="ri-delete-bin-line" />,
                  danger: true,
                  onClick: () => setDeleteTarget(item),
                },
              ]}
            />
          </td>
        )}
      </tr>
    ),
    [handleResendInvite, handleEdit, showTenantColumn, canManageUsers, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('tenantUsers.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <TableBox tableValues={values} />

      <Modal
        open={!!deactivateTarget}
        onClose={closeDeactivateModal}
        title={t('tenantUsers.list.pageTexts.deactivateModal.title')}
        content={
          <>
            <p>{t('tenantUsers.list.pageTexts.deactivateModal.message', { name: deactivateTarget?.name })}</p>
            <label className="form-label d-block" htmlFor="tenant-user-deactivate-reason">
              {t('tenantUsers.list.pageTexts.deactivateModal.reasonLabel')}
            </label>
            <textarea
              id="tenant-user-deactivate-reason"
              className="form-control"
              rows={2}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
            />
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeDeactivateModal} />
            <Button
              className="btn btn-danger"
              text={t('tenantUsers.list.pageTexts.deactivateModal.confirm')}
              onClick={handleDeactivateConfirm}
              loading={actionLoading}
            />
          </>
        }
      />

      <Modal
        open={!!deleteTarget}
        onClose={closeDeleteModal}
        title={t('tenantUsers.list.pageTexts.deleteModal.title')}
        content={
          <>
            <p>
              {deleteTarget?.invite_accepted_at
                ? t('tenantUsers.list.pageTexts.deleteModal.softMessage', { name: deleteTarget?.name })
                : t('tenantUsers.list.pageTexts.deleteModal.hardMessage', { name: deleteTarget?.name })}
            </p>
            <label className="form-label d-block" htmlFor="tenant-user-delete-reason">
              {t('tenantUsers.list.pageTexts.deleteModal.reasonLabel')}
            </label>
            <textarea
              id="tenant-user-delete-reason"
              className={`form-control ${deleteReasonError ? 'is-invalid' : ''}`}
              rows={2}
              value={deleteReason}
              onChange={(e) => {
                setDeleteReason(e.target.value);
                if (deleteReasonError) setDeleteReasonError('');
              }}
            />
            {deleteReasonError && <div className="text-danger mt-1 small">{deleteReasonError}</div>}
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeDeleteModal} />
            <Button
              className="btn btn-danger"
              text={t('tenantUsers.list.pageTexts.deleteModal.confirm')}
              onClick={handleDeleteConfirm}
              loading={actionLoading}
            />
          </>
        }
      />
    </>
  );
};

export default TenantUserListPanel;
